import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Logo } from '../../components/logo/logo';

@Component({
  selector: 'app-reset-password',
  imports: [CommonModule, ReactiveFormsModule, RouterLink, Logo],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.css',
})
export class ResetPassword implements OnInit {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  resetForm: FormGroup;
  isSubmitting = signal(false);
  showError = signal(false);
  errorMessage = signal('');
  showSuccess = signal(false);
  successMessage = signal('');
  showPassword = signal(false);
  showConfirmPassword = signal(false);
  isVerifying = signal(true);
  tokenValid = signal(false);

  private token = '';
  private email = '';

  constructor() {
    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      password_confirmation: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || '';
      this.email = params['email'] || '';

      if (!this.token || !this.email) {
        this.isVerifying.set(false);
        this.showError.set(true);
        this.errorMessage.set('Invalid reset link. Please request a new one.');
        return;
      }

      this.verifyToken();
    });
  }

  private verifyToken() {
    this.http.post<any>(`${environment.apiUrl}/verify-reset-token`, {
      email: this.email,
      token: this.token,
    }).subscribe({
      next: (res) => {
        this.isVerifying.set(false);
        if (res.status) {
          this.tokenValid.set(true);
        } else {
          this.showError.set(true);
          this.errorMessage.set(res.message || 'This reset link is invalid or has already been used.');
        }
      },
      error: (err) => {
        this.isVerifying.set(false);
        this.showError.set(true);
        this.errorMessage.set(err?.error?.message || 'This reset link is invalid or has already been used.');
      },
    });
  }

  onSubmit() {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    const { password, password_confirmation } = this.resetForm.value;

    if (password !== password_confirmation) {
      this.showError.set(true);
      this.errorMessage.set('Passwords do not match');
      return;
    }

    this.isSubmitting.set(true);
    this.showError.set(false);
    this.showSuccess.set(false);

    this.http.post<any>(`${environment.apiUrl}/reset-password`, {
      email: this.email,
      token: this.token,
      password,
      password_confirmation,
    }).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        if (res.status) {
          this.showSuccess.set(true);
          this.successMessage.set(res.message);
          // Redirect to login after 3 seconds
          setTimeout(() => this.router.navigate(['/login']), 3000);
        } else {
          this.showError.set(true);
          this.errorMessage.set(res.message || 'Something went wrong');
        }
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.showError.set(true);
        this.errorMessage.set(err?.error?.message || 'Something went wrong. Please try again.');
      },
    });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.resetForm.get(field);
    return !!(control && control.invalid && control.touched);
  }
}
